import { useQuery } from '@tanstack/react-query';

import { fetchLeagueContext } from '@api-service/league-context';
import type { LeagueContext } from '@api-service/league-context';

const ONE_MINUTE_IN_MS = 60 * 1000;
const FPL_UPDATE_POLL_INTERVAL_MS = 30 * 1000;

export type UseLeagueContextOptions = {
  enabled?: boolean;
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
};

export type UseLeagueContextResult = {
  context: LeagueContext | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
};

export const useLeagueContext = (
  options: UseLeagueContextOptions = {}
): UseLeagueContextResult => {
  const {
    enabled = true,
    staleTime = ONE_MINUTE_IN_MS,
    refetchOnWindowFocus = true
  } = options;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['league-context'],
    queryFn: fetchLeagueContext,
    enabled,
    staleTime,
    refetchOnWindowFocus,
    refetchInterval: (latest) =>
      latest?.isFplUpdating ? FPL_UPDATE_POLL_INTERVAL_MS : false
  });

  return {
    context: data ?? null,
    isLoading: isLoading && enabled,
    isError,
    refetch: () => {
      refetch();
    }
  };
};
